import { BUSINESSES_PATH } from "@/lib/navigation";
import { useCreateBusiness, useClientSearch } from "@/hooks/api-hooks";
import { ApiResponse } from "@/services/api-service";
import { Button, Group, Loader, Select, SelectProps, TextInput, Highlight } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Business, EMPTY_BUSINESS } from "@turizi/shared";
import { SELECT_BUSINESS_CATEGORIES } from "@/types/templates";
import { localTenant } from "@/services/session-service";
import { Link } from "react-router-dom";
import { IconCheck, IconExternalLink, IconMapPin, IconSearch, IconTag, IconId, IconMail, IconPhone } from "@tabler/icons-react";
import { notify } from "@/lib/notify";
import { TAG_ICONS, TagIcon } from "@/lib/tag-utils";
import CountrySelector from "./country-selector";
import { formatDayLong } from "@/lib/format-utils";

type Props = {
    onCreate?: (business: Business) => void;
    onClose?: () => void;
    preloadName?: string;
    name?: string;
    owner?: boolean;
}

const MIN_CHARS_TO_SEARCH = 3;

const getErrorMessage = (err: ApiResponse<unknown> | Error): string => {
    if ('message' in err && typeof err.message === 'string' && err.message) {
        return err.message;
    }
    if ('error' in err && err.error) {
        if (typeof err.error === 'string') {
            return err.error;
        }
        if (typeof err.error === 'object' && err.error.message) {
            return err.error.message;
        }
    }
    return 'error';
};

export default function CreateBusinessComponent({
    onCreate,
    onClose,
    preloadName,
    name = '',
    owner = false
}: Props): JSX.Element {
    const { t } = useTranslation();
    const categoriesData = SELECT_BUSINESS_CATEGORIES();
    const [createdBusiness, setCreatedBusiness] = useState<Business | null>(null);
    const initialName = preloadName || name || '';
    const [draft, setDraft] = useState<Partial<Business>>({
        category: undefined,
        name: initialName,
        country_code: '',
        tax_id_number: '',
        tax_condition: '5',
    });
    const [activeSearchValue, setActiveSearchValue] = useState<string>(initialName);
    const nameInputRef = useRef<HTMLInputElement>(null);

    const createBusinessMutation = useCreateBusiness();
    const tenant = localTenant();

    const getSearchQuery = () => {
        if (activeSearchValue.trim().length >= MIN_CHARS_TO_SEARCH) return activeSearchValue.trim();
        if (draft.name && draft.name.trim().length >= MIN_CHARS_TO_SEARCH) return draft.name.trim();
        if (draft.tax_id_number && draft.tax_id_number.trim().length >= MIN_CHARS_TO_SEARCH) return draft.tax_id_number.trim();
        return '';
    };

    const searchQuery = getSearchQuery();
    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);

    const {
        data: searchResponse,
        isLoading: isSearching
    } = useClientSearch({
        query: debouncedSearchQuery,
        categories: 'business',
    }, {
        enabled: debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH
    });

    const matchingBusinesses: Business[] = searchResponse?.data || [];

    const sortedBusinesses: Business[] = [...matchingBusinesses].sort((a, b) => {
        if (draft.category) {
            const aMatchesCat = a.category === draft.category;
            const bMatchesCat = b.category === draft.category;
            if (aMatchesCat && !bMatchesCat) return -1;
            if (!aMatchesCat && bMatchesCat) return 1;
        }
        const catA = a.category || '';
        const catB = b.category || '';
        if (catA !== catB) return catA.localeCompare(catB);
        return (a.name || '').localeCompare(b.name || '');
    });

    const canSave = Boolean(
        draft.name &&
        draft.name.trim().length >= MIN_CHARS_TO_SEARCH &&
        draft.category &&
        !isSearching &&
        !createBusinessMutation.isPending
    );

    const createNewBusiness = async () => {
        if (!canSave || !tenant) return;

        const payload: Partial<Business> & { is_owner?: string } = {
            ...draft,
            name: draft.name?.trim(),
            category: draft.category,
            country_code: draft.country_code || null,
            tax_id_number: draft.tax_id_number?.trim() || null,
            tax_condition: draft.tax_condition || '5',
            is_owner: owner ? 'yes' : 'no'
        };

        createBusinessMutation.mutate(payload, {
            onSuccess: (res: ApiResponse<Business>) => {
                if (res.data) {
                    const created = res.data;
                    setCreatedBusiness(created);
                    onCreate?.(created);
                    onClose?.();
                }
            },
            onError: (err: ApiResponse<Business> | Error) => {
                const errorMsg = getErrorMessage(err);
                notify.error(t(errorMsg), t('error'));
            }
        });
    };

    useEffect(() => {
        const inputName = preloadName || name;
        if (inputName) {
            setDraft(prev => ({ ...prev, name: inputName }));
            setActiveSearchValue(inputName);
        }
    }, [preloadName, name]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (nameInputRef.current) {
                nameInputRef.current.focus();
                nameInputRef.current.select();
            }
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            setDraft({ ...EMPTY_BUSINESS, category: undefined });
        }
    };

    const renderSelectOption: SelectProps['renderOption'] = ({ option, checked }) => {
        const Icon = TAG_ICONS[option.value as keyof typeof TAG_ICONS] || IconTag;
        return (
            <Group flex="1" gap="xs">
                <Icon size={16} stroke={1.5} style={{ opacity: 0.6 }} />
                {option.label}
                {checked && <IconCheck size={16} stroke={1.5} style={{ marginInlineStart: 'auto', opacity: 0.6 }} />}
            </Group>
        );
    };

    const SelectedIcon = draft.category ? (TAG_ICONS[draft.category as keyof typeof TAG_ICONS] || IconTag) : null;

    if (createdBusiness) {
        return (
            <div className="space-y-4 min-w-[450px]">
                <div className="border border-green-200 bg-green-50/50 rounded-xl p-4 flex flex-col gap-2">
                    <div className="text-xs text-green-700 font-bold uppercase tracking-wider">
                        {t('new_business')}
                    </div>
                    <Link
                        to={`${BUSINESSES_PATH}/${createdBusiness.id}`}
                        className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:border-slate-200 transition-all"
                        target="_blank"
                    >
                        <TagIcon name={createdBusiness.category} hideText noBackground size="xs" />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold text-blue-500 truncate">
                                {createdBusiness.name}
                            </div>
                            <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mt-0.5">
                                #{createdBusiness.id} · {t(createdBusiness.category)}
                            </div>
                        </div>
                        <IconExternalLink size={16} className="text-gray-400 flex-shrink-0" stroke={1.5} />
                    </Link>
                </div>
                <div className="flex items-center justify-end mt-8">
                    <Button
                        size="xs"
                        color="black"
                        radius="lg"
                        onClick={() => {
                            setCreatedBusiness(null);
                            setDraft({ ...draft, name: '', category: undefined, country_code: '', tax_id_number: '' });
                            setActiveSearchValue('');
                        }}
                    >
                        {t('create_another')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-full">
            <div className="flex flex-col md:flex-row gap-8 w-full max-w-full">
                {/* Left Column: Form Controls */}
                <div className="w-full md:w-[250px] md:flex-shrink-0 space-y-3">
                    <TextInput
                        ref={nameInputRef}
                        data-autofocus
                        autoFocus
                        tabIndex={0}
                        size="sm"
                        label={t('business_name')}
                        className="font-semibold"
                        value={draft.name || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            setDraft(prev => ({ ...prev, name: val }));
                            setActiveSearchValue(val);
                        }}
                        onKeyDown={handleKeyDown}
                        required
                    />
                    <Select
                        size="sm"
                        label={t('category')}
                        value={draft.category || null}
                        data={categoriesData}
                        onChange={(value) => setDraft(prev => ({ ...prev, category: value || undefined }))}
                        className="font-semibold"
                        searchable
                        required
                        renderOption={renderSelectOption}
                        leftSection={SelectedIcon ? <SelectedIcon size={16} stroke={1.5} /> : null}
                    />
                    <CountrySelector
                        size="sm"
                        value={draft.country_code || ''}
                        onChange={(value) => setDraft(prev => ({ ...prev, country_code: value || null }))}
                    />
                    <TextInput
                        size="sm"
                        label={t('tax_id_number')}
                        value={draft.tax_id_number || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            setDraft(prev => ({ ...prev, tax_id_number: val }));
                            setActiveSearchValue(val);
                        }}
                        onKeyDown={handleKeyDown}
                    />

                    {owner && (
                        <div className="text-xs text-gray-700 text-center p-2 border rounded-2xl border-gray-400/20 bg-gray-400/5">
                            {t('make_me_owner_of_this_business')}
                        </div>
                    )}

                    <div className="mt-6">
                        <Button
                            onClick={createNewBusiness}
                            loading={createBusinessMutation.isPending}
                            disabled={!canSave}
                            size="xs"
                            color="black"
                            radius="xl"
                            fullWidth
                        >
                            {t('create_new_business')}
                        </Button>
                    </div>
                </div>

                {/* Right Column: Search Results Matches */}
                <div className="w-full flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 select-none">
                            {t('existing_business_matches')}
                        </div>
                        {matchingBusinesses.length > 0 && (
                            <span className="text-[11px] font-semibold text-gray-700">
                                ({matchingBusinesses.length})
                            </span>
                        )}
                    </div>
                    <div className="space-y-2 p-1.5 w-full h-[300px] overflow-y-auto bg-slate-50/80 rounded-xl border border-slate-200/80 subtle-scrollbar">
                        {isSearching && (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs p-4 text-center select-none">
                                <Loader size="xs" color="gray" />
                            </div>
                        )}
                        {!isSearching && sortedBusinesses.map((biz: Business) => {
                            const fullAddressStr = [biz.city, biz.country_code].filter(Boolean).join(', ');
                            const highlightStyle = {
                                backgroundColor: 'rgba(254, 240, 138, 0.3)',
                                fontWeight: 700,
                                borderRadius: '3px',
                                padding: '0 2px'
                            };

                            return (
                                <div
                                    key={biz.id}
                                    className="group p-2.5 bg-white border border-slate-200/90 hover:border-gray-300 hover:bg-gray-50/60 rounded-lg transition-all cursor-pointer shadow-2xs hover:shadow-sm flex items-start justify-between gap-3"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatedBusiness(biz);
                                        onCreate?.(biz);
                                        onClose?.();
                                    }}
                                >
                                    {/* Left Category & ID Badge */}
                                    <div className="flex flex-col items-center justify-center text-gray-700 flex-shrink-0">
                                        <TagIcon name={biz.category || 'business'} size="xs" hideText />
                                        <span className="font-light text-[10px] text-gray-800 tracking-tight">#{biz.id}</span>
                                    </div>

                                    {/* Center Details */}
                                    <div className="min-w-0 flex-1 space-y-0.5">
                                        <div>
                                            <div className="flex items-baseline gap-1">
                                                <span className="font-semibold text-sm text-slate-900 group-hover:text-gray-700 transition-colors break-words">
                                                    <Highlight highlight={debouncedSearchQuery} highlightStyles={highlightStyle}>
                                                        {biz.name}
                                                    </Highlight>
                                                </span>
                                                <span className="text-xs font-light text-gray-500">
                                                    ({t(biz.category)})
                                                </span>
                                            </div>
                                            {biz.tax_name && biz.tax_name !== biz.name && (
                                                <div className="text-xs text-slate-500 font-normal italic">
                                                    <Highlight size="xs" highlight={debouncedSearchQuery} highlightStyles={highlightStyle}>
                                                        {biz.tax_name}
                                                    </Highlight>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-slate-500">
                                            {fullAddressStr && (
                                                <div className="flex items-center gap-1">
                                                    <IconMapPin size={12} stroke={1.8} className="text-slate-400 flex-shrink-0" />
                                                    <span className="break-words">
                                                        <Highlight size="xs" highlight={debouncedSearchQuery} highlightStyles={highlightStyle}>
                                                            {fullAddressStr}
                                                        </Highlight>
                                                    </span>
                                                </div>
                                            )}
                                            {biz.tax_id_number && (
                                                <div className="flex items-center gap-1 text-slate-700">
                                                    <IconId size={12} stroke={1.8} className="text-slate-400 flex-shrink-0" />
                                                    <span className="break-all ">
                                                        <Highlight size="xs" highlight={debouncedSearchQuery} highlightStyles={highlightStyle}>
                                                            {biz.tax_id_number}
                                                        </Highlight>
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right Select Affordance & External Link */}
                                    <Link
                                        to={`${BUSINESSES_PATH}/${biz.id}`}
                                        target="_blank"
                                        className="block text-slate-300 hover:text-gray-600 flex-shrink-0 transition-colors p-1"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="solid-box-border-tinted-hover text-xxs text-gray-500 flex items-center gap-1">
                                            {formatDayLong(biz.created_at)}
                                            <IconExternalLink size={12} />
                                        </div>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export { CreateBusinessComponent as CreateOrSelectBusinessComponent };